import { useCallback, useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, Pager, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { clp } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";
import { useCatalog } from "@/pages/rrhh/Employees";
import { STOCK_STATE } from "./ItemPicker";
import { ImportDialog } from "./ImportDialog";

const EMPTY = { sku: "", barcode: "", name: "", category: "", unit: "", unit_value: 0, min_stock: 0, is_tool: false, status: "activo" };

const ProductDialog = ({ edit, setEdit, onSaved, val }) => {
  const cats = useCatalog("categorias_producto");
  const units = useCatalog("unidades");
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  const save = async () => {
    const { id, ...body } = edit;
    try { id ? await api.patch(`/abastecimiento/products/${id}`, body) : await api.post("/abastecimiento/products", body); toast.success("Producto guardado"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="max-w-xl" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{edit?.id ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
        {edit && <div className="grid sm:grid-cols-2 gap-3">
          <Field label="SKU *"><Input data-testid="product-sku-input" value={edit.sku} onChange={(e) => set("sku", e.target.value.toUpperCase())} /></Field>
          <Field label="Código de barras"><Input data-testid="product-barcode-input" value={edit.barcode || ""} onChange={(e) => set("barcode", e.target.value)} /></Field>
          <div className="sm:col-span-2"><Field label="Nombre *"><Input data-testid="product-name-input" value={edit.name} onChange={(e) => set("name", e.target.value)} /></Field></div>
          <Field label="Categoría *"><Pick testid="product-category-select" value={edit.category} onChange={(v) => set("category", v)} options={cats} placeholder="Seleccione" /></Field>
          <Field label="Unidad *"><Pick testid="product-unit-select" value={edit.unit} onChange={(v) => set("unit", v)} options={units} placeholder="Seleccione" /></Field>
          {val && <Field label="Valor unitario (CLP)"><Input data-testid="product-value-input" type="number" min="0" value={edit.unit_value} onChange={(e) => set("unit_value", parseInt(e.target.value, 10) || 0)} /></Field>}
          <Field label="Stock mínimo (Bodega Central)"><Input data-testid="product-min-input" type="number" min="0" step="any" value={edit.min_stock} onChange={(e) => set("min_stock", parseFloat(e.target.value) || 0)} /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="product-tool-switch" checked={edit.is_tool} onCheckedChange={(v) => set("is_tool", v)} />Herramienta con seguimiento (Fase 4B)</label>
          {edit.id && <label className="flex items-center gap-2 text-sm"><Switch data-testid="product-active-switch" checked={edit.status === "activo"} onCheckedChange={(v) => set("status", v ? "activo" : "inactivo")} />Activo</label>}
        </div>}
        <DialogFooter><Button data-testid="product-save-btn" className="rounded-full" onClick={save}>Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Products() {
  const { can } = useAuth();
  const val = can("abastecimiento.valorizacion.ver");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 25 });
  const [edit, setEdit] = useState(null);
  const [imp, setImp] = useState(false);
  const load = useCallback(() => api.get("/abastecimiento/products", { params: { q, page, status: "" } }).then((r) => setData(r.data)), [q, page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="products-page">
      <PageHeader eyebrow="Abastecimiento" title="Productos" subtitle="Maestro de ítems con su valor y stock mínimo. El estado se calcula sobre la Bodega Central."
        actions={can("abastecimiento.productos.gestionar") && <>
          <Button data-testid="import-products-btn" variant="outline" className="rounded-full" onClick={() => setImp(true)}>Carga masiva (Excel)</Button>
          <Button data-testid="create-product-btn" className="rounded-full" onClick={() => setEdit({ ...EMPTY })}><Plus size={16} className="mr-1" />Nuevo producto</Button></>} />
      <div className="syc-card overflow-hidden">
        <div className="p-4 border-b border-[#e4e8e1] relative"><Search size={16} className="absolute left-7 top-1/2 -translate-y-1/2 text-[#8a9584]" />
          <Input data-testid="products-search-input" className="pl-9 max-w-md" placeholder="Buscar por SKU, nombre o código de barras" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} /></div>
        {data.items.length === 0 ? <EmptyState title="Sin productos" /> : data.items.map((p) => (
          <button key={p.id} data-testid={`product-row-${p.id}`} disabled={!can("abastecimiento.productos.gestionar")} onClick={() => setEdit({ ...EMPTY, ...p })}
            className={`w-full flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2] text-sm text-left ${p.status === "activo" ? "" : "opacity-50"}`}>
            <span className="font-mono text-xs w-28">{p.sku}</span>
            <div className="flex-1 min-w-[180px]"><div className="font-semibold">{p.name}</div><div className="text-xs text-[#5b6656]">{p.category} · {p.unit}{p.is_tool ? " · herramienta" : ""}</div></div>
            <span className="text-xs">Stock <b>{p.stock_total}</b> · mín. {p.min_stock}</span>
            {val && <span className="text-xs w-24 text-right">{clp(p.unit_value)}</span>}
            <StatusBadge testid={`product-state-${p.id}`} status={STOCK_STATE[p.central_state][0]} label={STOCK_STATE[p.central_state][1]} />
          </button>))}
        <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      </div>
      <ProductDialog edit={edit} setEdit={setEdit} val={val} onSaved={() => { setEdit(null); load(); }} />
      <ImportDialog open={imp} onOpenChange={setImp} onDone={() => { setImp(false); load(); }} />
    </div>
  );
}
